/**
 * DiagQuoteHydration: Diagnostics drawer -- quote hydration, shown once, moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { AnalyticsMetric, badgeStyle, secondaryText, ui } from "@components/screener/screenerUi";
import { numberFmt, pctFmt, titleizeCode } from "@lib/screenerTruth";
import { type ChainAudit } from "@lib/screenerTypes";

export type DiagQuoteHydrationProps = {
  chainAudit: ChainAudit | null;
  quoteHydration: any;
  quoteHydrationSummary: { resolved: number; rowsReturned: number; quoteComplete: number; greekComplete: number; fullComplete: number; incomplete: number; pct: number | null; mode: string; elapsedMs: number; singletonAttempted: number; statusCounts: any; };
  quoteHydrationTone: "neutral" | "success" | "warning" | "danger";
};

export default function DiagQuoteHydration({
  chainAudit,
  quoteHydration,
  quoteHydrationSummary,
  quoteHydrationTone,
}: DiagQuoteHydrationProps) {
  return (
    <div
      className="rounded-2xl border p-3 text-sm"
      data-testid="screener-quote-hydration"
      style={{ borderColor: ui.cardBorder, backgroundColor: quoteHydrationSummary.resolved > 0 && !quoteHydrationSummary.incomplete ? "rgba(16, 185, 129, 0.08)" : "rgba(245, 158, 11, 0.08)" }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold">Quote hydration</div>
          <div className="mt-1" style={secondaryText}>
            Robust hydration preflights the post-OI conids, waits for IBKR streams to populate, polls again, then singleton-retries incomplete rows before final ranking.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span style={badgeStyle(quoteHydrationTone)}>
            {quoteHydrationSummary.resolved <= 0
              ? "Not reported"
              : quoteHydrationSummary.incomplete
              ? "Incomplete rows remain"
              : "Hydrated"}
            {quoteHydration?.status || chainAudit?.quoteHydrationStatus ? ` · ${String(quoteHydration?.status || chainAudit?.quoteHydrationStatus).toUpperCase()}` : ""}
          </span>
          <span style={badgeStyle("neutral")}>Mode: {titleizeCode(quoteHydrationSummary.mode)}</span>
          <span style={badgeStyle("neutral")}>Singletons: {numberFmt(quoteHydrationSummary.singletonAttempted, 0)}</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <AnalyticsMetric label="Quote + Greek complete" value={`${numberFmt(quoteHydrationSummary.fullComplete, 0)} / ${numberFmt(quoteHydrationSummary.resolved, 0)}`} subtext={`${pctFmt(quoteHydrationSummary.pct, 0)} complete; ${numberFmt(quoteHydrationSummary.incomplete, 0)} still incomplete`} />
        <AnalyticsMetric label="Rows returned" value={`${numberFmt(quoteHydrationSummary.rowsReturned, 0)} / ${numberFmt(quoteHydrationSummary.resolved, 0)}`} subtext="Any IBKR snapshot row returned" />
        <AnalyticsMetric label="Midpoint complete" value={`${numberFmt(quoteHydrationSummary.quoteComplete, 0)} / ${numberFmt(quoteHydrationSummary.resolved, 0)}`} subtext="Bid/ask or last-derived midpoint available" />
        <AnalyticsMetric label="Greek complete" value={`${numberFmt(quoteHydrationSummary.greekComplete, 0)} / ${numberFmt(quoteHydrationSummary.resolved, 0)}`} subtext="Positive vega available" />
        <AnalyticsMetric label="Attempts" value={numberFmt((quoteHydration?.attempts || []).length || null, 0)} subtext={quoteHydration?.interpretation || "IBKR quote/Greek warm-up passes"} />
        <AnalyticsMetric label="Elapsed" value={quoteHydrationSummary.elapsedMs ? `${numberFmt(quoteHydrationSummary.elapsedMs, 0)} ms` : "—"} subtext="Backend snapshot hydration time" />
      </div>
    </div>
  );
}
